import React from 'react';

function TopicsPage() {
    return (
        <>
            <h2>Web Development Concepts</h2>
            <nav class="local">
                <a href="#webServers">Web Servers</a>
                <a href="#frontendDesign">Frontend Design</a>
                <a href="#optimizingImages">Optimizing Images</a>
                <a href="#favicons">Favicons</a>
                <a href="#cascadingStyleSheets">Cascading Style Sheets</a>
            </nav>
            <article id="webServers">
                <h3>Web Servers</h3>
                <p>
                    The term <strong>index</strong> as it relates to
                    websites and servers refers to
                    the main file in a basic HTML website's structure. In
                    the context
                    of websites, the index refers to the root file in the
                    hierarchy of
                    the website's file layout. The index.html file is
                    typically the
                    first file a web developer deals with when creating a
                    website.
                    Furthermore, the term index, as it relates to <strong>servers</strong>,
                    refers
                    to the default file that the server will respond with
                    when a
                    client makes a request to a directory. So, for example,
                    if a
                    client requests a URL like 'www.example.com' which does
                    not
                    provide a path to any <strong>resource</strong>, the
                    server will look for
                    'index.html' in the root directory to serve up as the
                    website's
                    home page.
                </p>

                <p>
                    When inspecting the browser's <strong>Web Dev/Inspector
                        tools</strong>, it is
                    apparent that there are a few distinct differences
                    between
                    the local file and the one that's hosted on my OSU
                    web server. For one, I immediately noticed on the web
                    server,
                    there is a <strong>favicon</strong> included. When
                    analyzing the <strong>headers</strong>, I see
                    that they both have response <strong>200</strong> and
                    that there is an <strong>IP address</strong>
                    assigned to my OSU web server hosted site, and there is
                    no remote
                    address on my local file. There are many more key-value
                    pairs
                    included in the contents of the <strong>response and
                        request headers</strong> on
                    the web server file.
                </p>

                <p>
                    Regarding the favicon.ico file, it appears that it has
                    status 200,
                    but the main.css and main.js files report <strong>status
                        404</strong>. This is due
                    to the fact that I requested these files with my <strong>link
                        and script tags</strong>,
                    however there exists no such files on the web server, as
                    I did not actually include any CSS or JS files. The Web
                    Dev/Inspector
                    tools are telling me that these files do not exist and
                    this equates to a
                    status 404 with <strong>reason phrase</strong>
                    'Not Found'.
                </p>

                <p>
                    My website's <strong>URL</strong> happens to be
                    <code><strong>https://web.engr.oregonstate.edu/~bryanni/Module%204/a4-bryanni/index.html</strong></code>.
                    Breaking it down we have the <strong>scheme</strong>,
                    which is a secure hypertext transfer protocol: '<code>https</code>'.
                    The <strong>sub-domain</strong> is '<code>web.engr</code>' and the
                    <strong>host-domain</strong> is
                    '<code>oregonstate.edu</code>'. The <strong>path to resources</strong>
                    is "<code>bryanni/Module%204/a4-bryanni/index.html</code>".
                </p>
            </article>

            <article id="frontendDesign">
                <h3>Frontend Design</h3>

                <p>
                    Frontend design revolves around crafting an optimal user
                    experience. It encompasses the
                    aesthetic layout of the page, the graphical user
                    interface (GUI), and the user interaction
                    aspects. The visual design requires a harmonious color
                    palette, consistent font styles and
                    typography, suitable imagery including icons and
                    illustrations, and intuitive navigation systems.
                </p>
                <h4>The Five "E"s of Usability</h4>
                <dl>
                    <dt><strong>Effective</strong></dt>
                    <dd>Helps users achieve their goals accurately. The
                        design should guide the user to reach their
                        desired outcome effectively.
                    </dd>

                    <dt><strong>Efficient</strong></dt>
                    <dd>Allows users to complete tasks with the least number
                        of steps possible. It aims to provide a
                        fast and smooth user experience.
                    </dd>

                    <dt><strong>Easy to navigate</strong></dt>
                    <dd>Provides an intuitive navigation system that is easy
                        for first-time users to understand.
                        It should allow users to locate their goals easily,
                        and remember the process for future use.
                    </dd>

                    <dt><strong>Error-free</strong></dt>
                    <dd>Minimizes accessibility and availability issues. The
                        design should learn from common user
                        errors to avoid possible roadblocks, creating a
                        seamless experience for the user's specific needs.
                    </dd>

                    <dt><strong>Enjoyable/engaging</strong></dt>
                    <dd>Delivers a user experience that is not only
                        effective, efficient, and easy to navigate,
                        but also enjoyable and engaging. The design should
                        cater to the unique needs of the intended
                        audience in both content and design, ensuring they
                        return because of the overall positive experience.
                    </dd>
                </dl>

                <h4>Linking Mechanisms and Anchoring Content</h4>
                <ol>
                    <li>Utilizing <strong>anchors</strong>, we can link to
                        external resources. This is accomplished using the
                        <code>&lt;a&gt;</code> tag, thus forming a <strong>hyperlink</strong>.
                        The content nestled between the opening and closing
                        <code>&lt;a&gt;</code> tags serves as the descriptor
                        of the <strong>link</strong>. Upon the user's click,
                        the browser <strong>navigates</strong> to the
                        destination page. The href attribute embedded in the
                        opening <code>&lt;a&gt;</code> tag delineates the
                        <strong>URL/path</strong> the user will be directed
                        to upon clicking the link.
                    </li>
                    <li><strong>Anchors</strong> can also connect to
                        internal elements within the same webpage. The
                        methodology for linking to internal content is
                        twofold. Firstly, the <strong>element</strong>
                        intended for navigation, such as an <code>&lt;article&gt;</code>,
                        should carry an <strong>id</strong> in the opening
                        tag in the format <strong>id="name"</strong>.
                        Secondly, the opening <code>&lt;a&gt;</code> tag of
                        the link should bear the <strong>href</strong>
                        attribute in the format <strong>href="#name"</strong>,
                        where the <strong>id</strong> name is preceded by a
                        <strong>hash</strong>. On clicking the link, the
                        user will be transported to the <code>&lt;article&gt;</code>
                        marked with the specific <strong>id="name"</strong>.
                    </li>
                    <li>Linking between pages is made possible by <strong>anchors</strong>
                        in a <code>&lt;nav&gt;</code> tag, which defines the
                        <strong>navigation</strong> element. Enclosed in the
                        <code>&lt;nav&gt;</code> tag, every <strong>link</strong>
                        leading to a different page is characterized by an
                        <code>&lt;a&gt;</code> tag. The content enclosed
                        between the <code>&lt;a&gt;</code> tags specifies
                        the destination page of the link. To define each
                        link, a <strong>href</strong> attribute is appended
                        to the opening <code>&lt;a&gt;</code> tag to
                        indicate the <strong>relative path</strong> of the
                        target page, as in <strong>href="index.html"</strong>.
                    </li>
                </ol>

            </article>

            <article id="optimizingImages">
                <h3>Optimizing Images</h3>
                <p> Images play an important role in
                    enhancing the overall
                    look
                    and feel of a website. They serve as an effective tool
                    for
                    illustrating ideas and concepts, thus helping to engage
                    users.
                    However, it's important to ensure that images are
                    <strong>optimized for
                        the web</strong> to provide an optimal user
                    experience. This
                    involves
                    considering several factors:
                </p>

                <p> Choosing the correct <strong>file format</strong> is
                    crucial. Photos are
                    usually
                    saved in <strong>.JPG format</strong> because of its
                    ability to handle
                    millions
                    of colors while maintaining a relatively small file
                    size, making
                    it a great choice for photographs and complex images.
                    Line-art
                    images like logos and icons are usually <strong>.GIF</strong>
                    or
                    sometimes 8-bit
                    <strong>.PNG</strong> due to their ability to render
                    sharp lines and
                    solid colors
                    effectively. Graphics with true transparency need to be
                    in 24-bit
                    <strong>.PNG</strong> as they can handle alpha channel
                    transparency.
                </p>

                <p> The <strong>SVG (Scalable Vector Graphics)</strong>
                    format is excellent
                    for
                    vector-based graphics like logos, icons, and
                    illustrations, as
                    it provides clear and crisp images regardless of the
                    resolution
                    or zoom level. The <strong>WebP format</strong>, a
                    modern image format
                    developed
                    by Google, provides superior lossless and lossy
                    compression for
                    images, making it ideal for web use. However, WebP's
                    compatibility
                    across all browsers is not yet universal, so its usage
                    might be
                    limited based on the target audience. </p>
            </article>

            <article id="favicons">
                <h3>Favicons</h3>
                <p> Browsers and devices use <strong>favicons</strong> as
                    small icons that
                    represent a website or a web page. Favicons are
                    typically
                    displayed in the address bar, in the tabs of the
                    browser,
                    and in the bookmarks bar. They are used to quickly
                    identify
                    and locate a webpage or site among a large number of
                    open
                    tabs or bookmarks. Favicons typically come in <strong>.ico</strong>,
                    <strong>.png</strong>,
                    and <strong>.svg</strong> formats.
                </p>
            </article>

            <article id="cascadingStyleSheets">
                <h3>Cascading Style Sheets</h3>
                <p>Cascading Style Sheets (CSS) are a vital part of web and
                    app development, primarily serving to override the
                    default visual style provided by HTML. By allowing each
                    element to be styled in a way that is more usable,
                    readable, and in line with brand requirements, CSS
                    significantly enhances the user experience. The main
                    reasons to incorporate CSS in websites and apps include
                    better control over the layout and appearance of the
                    webpage, improved website maintainability, and the
                    ability to adapt the presentation to different types of
                    devices.</p>

                <p>There are essentially <strong>five ways to incorporate
                    styles</strong> in your webpages or applications.
                    The most preferred method is the use of <strong>External
                        CSS</strong>. This involves redefining an element's
                    selector in one or more external files with a .css file
                    extension. For example, a main or global stylesheet can
                    be linked in the global head area of a website,
                    providing styles to all pages of the website that link
                    to that single, global stylesheet.</p>

                <p>Another method is the use of <strong>Component-specific
                    stylesheets</strong>. These stylesheets are specific
                    to components and can be linked after the global .css
                    file or imported in the global stylesheet.</p>

                <p>The third way is the use of <strong>Embedded CSS</strong>.
                    Here, styles are embedded directly within a style tag in
                    the HTML document. This method is typically used for
                    one-off style changes only.</p>

                <p>The fourth approach is the use of <strong>Inline CSS</strong>.
                    This involves incorporating styles directly within an
                    HTML element using an attribute and value.</p>

                <p>The fifth and final method involves incorporating styles
                    within a <strong>JavaScript function</strong>. In this
                    method, styles can be defined in JavaScript template
                    literals or manipulated through the Document Object
                    Model (DOM).</p>

                <p>Incorporating CSS in an external .css file improves
                    maintainability since the styles are in a separate file
                    and do not clutter the main .html file. This structure
                    makes the HTML file easier to understand and manage,
                    thus significantly improving the efficiency of the web
                    or app development process.</p>
            </article>
        </>
    );
}

export default TopicsPage;